// src/components/admin/sections/AdminPrograms.tsx - VERSIÓN FINAL COMPLETA
import React, { useState, useEffect, useRef } from 'react';
import apiElCristo, { Program, ProgramsData } from '../../../service/api_elcristo';
import './AdminPrograms.css';

const AdminPrograms: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<{ program: Program, index: number } | null>(null);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const titleEditorRef = useRef<HTMLDivElement>(null);

  const [programsData, setProgramsData] = useState<ProgramsData>({
    title: '',
    subtitle: '',
    programs: []
  });

  const [formData, setFormData] = useState<Program>({
    image: '',
    title: '',
    ageRange: '',
    description: '',
    link: '',
    backgroundColor: 'default'
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (titleEditorRef.current && programsData.title) {
      titleEditorRef.current.innerHTML = programsData.title;
    }
  }, [programsData.title]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getProgramsData();
      console.log('📥 Programs data:', data);
      setProgramsData(data);
    } catch (error) {
      console.error('❌ Error loading:', error);
      setMessage({ type: 'error', text: 'Error al cargar programas' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProgramsData(prev => ({ ...prev, [name]: value }));
  };

  const handleTitleInput = () => {
    if (titleEditorRef.current) {
      setProgramsData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
    }
  };

  const applyFormat = (editorRef: React.RefObject<HTMLDivElement>, tag: string) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (selectedText) {
      const span = document.createElement('span');
      span.className = tag;
      span.textContent = selectedText;
      range.deleteContents();
      range.insertNode(span);
      
      if (editorRef === titleEditorRef) {
        setProgramsData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
      }
    }
  };

  const clearFormat = (editorRef: React.RefObject<HTMLDivElement>) => {
    if (!editorRef.current) return;
    
    const plainText = editorRef.current.innerText;
    editorRef.current.innerHTML = plainText;
    
    if (editorRef === titleEditorRef) {
      setProgramsData(prev => ({ ...prev, title: plainText }));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: '❌ Solo PNG, JPG, SVG o WEBP' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: '❌ Máximo 2MB' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      setUploadingImage(true);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', 'programs');
      
      const response = await fetch('https://backend.anubisai.net/api/elcristo/admin/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('elcristo_token')}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al subir');
      }
      
      const data = await response.json();
      
      setFormData(prev => ({ ...prev, image: data.url }));
      
      setMessage({ type: 'success', text: '✅ Imagen subida' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: `❌ ${err.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleOpenModal = (program?: Program, index?: number) => {
    if (program && index !== undefined) {
      setEditingProgram(program);
      setEditingIndex(index);
      setFormData({ ...program });
    } else {
      setEditingProgram(null);
      setEditingIndex(null);
      setFormData({
        image: '',
        title: '',
        ageRange: '',
        description: '',
        link: '',
        backgroundColor: 'default'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProgram(null);
    setEditingIndex(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProgram = async () => {
    if (!formData.title || !formData.ageRange || !formData.description || !formData.image) {
      setMessage({ type: 'error', text: '❌ Completa todos los campos' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setSaving(true);

    try {
      let updatedPrograms;
      
      if (editingIndex !== null) {
        updatedPrograms = [...programsData.programs];
        updatedPrograms[editingIndex] = formData;
      } else {
        updatedPrograms = [...programsData.programs, formData];
      }

      const updatedData = { ...programsData, programs: updatedPrograms };
      
      console.log('📤 Guardando programa:', updatedData);
      
      await apiElCristo.updateSiteContent({ programs: updatedData });
      
      setProgramsData(updatedData);
      handleCloseModal();
      
      setMessage({ type: 'success', text: '✅ Programa guardado' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteModal = (program: Program, index: number) => {
    setProgramToDelete({ program, index });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setProgramToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!programToDelete) return;

    setSaving(true);

    try {
      const updatedPrograms = programsData.programs.filter((_, i) => i !== programToDelete.index);
      const updatedData = { ...programsData, programs: updatedPrograms };
      
      console.log('📤 Eliminando programa:', updatedData);
      
      await apiElCristo.updateSiteContent({ programs: updatedData });
      
      setProgramsData(updatedData);
      handleCloseDeleteModal();
      
      setMessage({ type: 'success', text: '✅ Programa eliminado' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveProgram = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= programsData.programs.length) return;

    const programs = [...programsData.programs];
    [programs[index], programs[newIndex]] = [programs[newIndex], programs[index]];
    setProgramsData(prev => ({ ...prev, programs }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      console.log('📤 Guardando cambios generales:', programsData);
      
      await apiElCristo.updateSiteContent({ programs: programsData });
      
      setMessage({ type: 'success', text: '✅ Cambios guardados' });
      setTimeout(() => setMessage(null), 3000);
      
      await loadData();
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
    } finally {
      setSaving(false);
    }
  };

  const getBgColorClass = (bg: string) => {
    switch (bg) {
      case 'bg-2': return 'bg-gray';
      case 'bg-4': return 'bg-blue';
      default: return 'bg-white';
    }
  };

  if (loading) {
    return (
      <div className="admin-programs-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-programs-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-graduation-cap"></i> Programas Educativos</h1>
          <p>Administrar niveles y programas</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-plus"></i>
          Nuevo Programa
        </button>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check' : 'exclamation'}-circle`}></i>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-cog"></i> Configuración</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label><i className="fas fa-tag"></i> Subtítulo</label>
              <input
                type="text"
                name="subtitle"
                value={programsData.subtitle}
                onChange={handleSectionChange}
                placeholder="Ej: Nuestros Programas"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label><i className="fas fa-heading"></i> Título</label>
              
              <div className="editor-toolbar">
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(titleEditorRef, 'elcristo-primary')}>
                  <i className="fas fa-font" style={{ color: '#ff0f00' }}></i> Rojo
                </button>
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(titleEditorRef, 'elcristo-blue')}>
                  <i className="fas fa-font" style={{ color: '#70A6B1' }}></i> Azul
                </button>
                <button type="button" className="toolbar-btn" onClick={() => clearFormat(titleEditorRef)}>
                  <i className="fas fa-eraser"></i> Limpiar
                </button>
              </div>

              <div
                ref={titleEditorRef}
                className="rich-editor"
                contentEditable
                onInput={handleTitleInput}
                suppressContentEditableWarning
                data-placeholder="Título de la sección"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-list"></i> Programas ({programsData.programs.length})</h3>
          </div>
          <div className="card-body">
            {programsData.programs.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-graduation-cap"></i>
                <p>No hay programas</p>
                <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
                  <i className="fas fa-plus"></i>
                  Crear Programa
                </button>
              </div>
            ) : (
              <div className="programs-grid">
                {programsData.programs.map((program, index) => (
                  <div key={index} className={`program-card ${getBgColorClass(program.backgroundColor)}`}>
                    <div className="program-card-drag">
                      <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    <div className="program-card-icon">
                      <img 
                        src={program.image.startsWith('http') 
                          ? program.image 
                          : `${window.location.origin}${program.image}`
                        } 
                        alt={program.title} 
                      />
                    </div>
                    
                    <div className="program-card-content">
                      <h4>{program.title}</h4>
                      <div className="program-meta">
                        <span className="age-badge">
                          <i className="fas fa-child"></i>
                          {program.ageRange}
                        </span>
                        <span className="bg-badge">{program.backgroundColor}</span>
                      </div>
                      <p>{program.description}</p>
                      {program.link && (
                        <a href={program.link} className="program-link" target="_blank" rel="noopener noreferrer">
                          <i className="fas fa-external-link-alt"></i>
                        </a>
                      )}
                    </div>
                    
                    <div className="program-card-actions">
                      <button type="button" className="btn-icon" onClick={() => handleMoveProgram(index, 'up')} disabled={index === 0}>
                        <i className="fas fa-arrow-up"></i>
                      </button>
                      <button type="button" className="btn-icon" onClick={() => handleMoveProgram(index, 'down')} disabled={index === programsData.programs.length - 1}>
                        <i className="fas fa-arrow-down"></i>
                      </button>
                      <button type="button" className="btn-icon btn-edit" onClick={() => handleOpenModal(program, index)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button type="button" className="btn-icon btn-delete" onClick={() => handleOpenDeleteModal(program, index)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i> Recargar
          </button>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <><div className="spinner-small"></div> Guardando...</>
            ) : (
              <><i className="fas fa-save"></i> Guardar Cambios</>
            )}
          </button>
        </div>
      </form>

      {/* PREVIEW SECTION */}
      <div className="programs-preview-section">
        <div className="programs-preview-header">
          <h3><i className="fas fa-eye"></i> Vista Previa</h3>
        </div>
        
        <div className="programs-preview">
          <div className="preview-section-title">
            <span className="preview-subtitle">{programsData.subtitle || 'Subtítulo'}</span>
            <h2 
              className="preview-title"
              dangerouslySetInnerHTML={{ 
                __html: programsData.title || 'Título <span class="elcristo-primary">Principal</span>' 
              }}
            />
          </div>

          <div className="preview-programs-grid">
            {(programsData.programs.length > 0 ? programsData.programs : [
              { image: '/assets/kidsa/assets/img/program/icon-1.png', title: 'Pre-Kínder', ageRange: '4-5 años', description: 'Programa educativo inicial', link: '#', backgroundColor: 'default' },
              { image: '/assets/kidsa/assets/img/program/icon-2.png', title: 'Kínder', ageRange: '5-6 años', description: 'Desarrollo integral', link: '#', backgroundColor: 'bg-2' },
              { image: '/assets/kidsa/assets/img/program/icon-3.png', title: 'Primaria', ageRange: '6-12 años', description: 'Educación básica', link: '#', backgroundColor: 'bg-4' }
            ]).map((program, index) => (
              <div key={index} className="preview-program-item">
                <div className={`preview-program-bg ${program.backgroundColor || 'default'}`}></div>
                <div className="preview-program-image">
                  <img 
                    src={program.image.startsWith('http') 
                      ? program.image 
                      : `${window.location.origin}${program.image}`
                    } 
                    alt={program.title} 
                  />
                </div>
                <div className="preview-program-content">
                  <h4>{program.title}</h4>
                  <span className="preview-age-range">({program.ageRange})</span>
                  <p>{program.description}</p>
                  <a href={program.link} className="preview-arrow-icon">
                    <i className="fas fa-arrow-right"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MODAL CREAR/EDITAR */}
      {showModal && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '700px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-graduation-cap"></i>
                {editingProgram ? 'Editar Programa' : 'Nuevo Programa'}
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Título *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Ej: Pre-Kínder"
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Rango Edad *</label>
                  <input
                    type="text"
                    name="ageRange"
                    value={formData.ageRange}
                    onChange={handleChange}
                    placeholder="Ej: 4-5 años"
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Color Fondo *</label>
                  <select name="backgroundColor" value={formData.backgroundColor} onChange={handleChange} className="form-control">
                    <option value="default">Blanco</option>
                    <option value="bg-2">Gris claro</option>
                    <option value="bg-4">Azul claro</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Link (opcional)</label>
                  <input
                    type="text"
                    name="link"
                    value={formData.link}
                    onChange={handleChange}
                    placeholder="#programas"
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Descripción *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Descripción del programa"
                  rows={4}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Icono/Imagen *</label>
                
                {formData.image && (
                  <div className="icon-preview-modal">
                    <img 
                      src={formData.image.startsWith('http') 
                        ? formData.image 
                        : `${window.location.origin}${formData.image}`
                      } 
                      alt="Preview" 
                    />
                  </div>
                )}
                
                <div className="image-upload-group">
                  <input
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder="/assets/kidsa/assets/img/program/icon-1.png"
                    className="form-control"
                  />
                  
                  <button
                    type="button"
                    className="btn btn-upload"
                    disabled={uploadingImage}
                    onClick={() => document.getElementById('program-img')?.click()}
                  >
                    {uploadingImage ? (
                      <><div className="spinner-small"></div> Subiendo...</>
                    ) : (
                      <><i className="fas fa-upload"></i> Subir</>
                    )}
                  </button>
                  <input
                    id="program-img"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </div>
                <small className="hint">
                  <i className="fas fa-info-circle"></i> PNG, JPG, SVG, WEBP (máx 2MB)
                </small>
              </div>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveProgram} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Guardando...</>
                ) : (
                  <>{editingProgram ? 'Actualizar' : 'Agregar'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DELETE */}
      {showDeleteModal && programToDelete && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '500px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ff0f00' }}></i>
                Confirmar Eliminación
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseDeleteModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                ¿Eliminar programa <strong>"{programToDelete.program.title}"</strong>?
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                No se puede deshacer.
              </p>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseDeleteModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Eliminando...</>
                ) : (
                  <><i className="fas fa-trash"></i> Eliminar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPrograms;